import {
    call,
    put,
    select
} from 'typed-redux-saga';
import { IAction, IAppConfig, IAppInitConfig } from 'types';
import { apiRequest } from 'api';

import { getOmjsProjectInstance, getAuthToken, getOperationData } from 'selectors';
import {
    loadCaptcha, isTokenValid, directLogin, directLogout, getUserInfo
} from 'api/app';
import i18next from 'i18next';
import { AuthorizationRequiredError } from 'utils/errors';
import { OmjsService } from 'services/omjs/omjs-service';
import { isMobile } from 'react-device-detect';
import {
    Service,
    bindActionsTyped,
    leading, every, onStart
} from '../service-utils';
import { operation, OperationsService } from '../operations/operations-service';
import { appActions as A } from './app-reducer-actions';

interface IInitializePayload {
    projectId: string,
    mapCode: string,
    mapName: string,
    apiHost: string
}

const monkeyPatchInitConfig = (config: IAppInitConfig) => {
    config.apiUrl = config.apiUrl.replace('//map.lenoblinvest.ru', __PROXY_URL__);
};
const monkeyPatchConfig = (config: IAppConfig) => {
    config.apiUrl = config.apiUrl.replace('//map.lenoblinvest.ru', __PROXY_URL__);
    config.styleUrl = config.styleUrl.replace('//map.lenoblinvest.ru', __PROXY_URL__);
    config.tilesUrl = config.tilesUrl.replace('//map.lenoblinvest.ru', __PROXY_URL__);
};

export interface IFolderStateOperationData {
    [key: string]: boolean
}

export class AppService extends Service {
    static serviceName = 'AppService';
    static generatedStopActionTypes: string[] = [];
    static generatedWatchers: Function[] = [];

    /* eslint-disable require-yield */
    @onStart
    static* onAppServiceStart() {
        /* Старая логика определения языка по get параметру lng */
        let match = (`${window.location.search}`).match(/[^\w]lng=(\w{2})([^\w]|$)/);
        let lng = 'ru';
        if (match && match[1]) {
            /* eslint-disable-next-line */
            lng = match[1];
        }

        i18next.changeLanguage(lng);
    }
    /* eslint-enable require-yield */

    @leading
    @operation('APP_INITIALIZE')
    static* initialize(action: IAction<IInitializePayload>) {
        const { projectId, mapCode, mapName, apiHost } = action.payload;

        apiRequest.initApiClient(projectId, mapCode, apiHost);
        // TODO: нужно в урлу добавить параметры версии токена и языка
        const { data: initConfig } = yield* call([ apiRequest, apiRequest.getInitConfig ]);

        if (__PROXY_URL__) {
            monkeyPatchInitConfig(initConfig);
        }

        yield put(A.appSetInitConfig(initConfig));
        apiRequest.setOmsApiBaseUrl(initConfig.apiUrl.replace('%version', window['omjs'].version));

        let savedAuthToken = yield* select(getAuthToken);
        let isSavedTokenValid = false;
        if (savedAuthToken) {
            isSavedTokenValid = yield* call(isTokenValid, savedAuthToken);
        }

        if (!isSavedTokenValid) {
            yield put(A.appSetAuthToken(null));
        }

        if (!initConfig.authorization_allow) {
            yield put(A.appSetAuthToken(null));
        }

        if (initConfig.authorizationRequired && !isSavedTokenValid) {
            throw new AuthorizationRequiredError();
        }

        let config: IAppConfig;
        let { data } = yield* call([ apiRequest, apiRequest.getConfig ], savedAuthToken);
        config = data;

        let tempConfig = config;

        if (omjs.plugins && omjs.plugins.length) {
            tempConfig = JSON.parse(JSON.stringify(config));

            omjs.plugins.forEach((plugin) => {
                if (plugin.beforeViewInit) {
                    tempConfig = plugin.beforeViewInit(tempConfig);
                }
            });
        }

        yield put(A.appSetConfig({ ...tempConfig, projectId }));
        if (i18next.language === 'en') {
            data.publication.base_maps.forEach(bMap => {
                if (bMap.type === 'XYZ') {
                    bMap.value = `${bMap.value}&lng=en`;
                }
            });
        }

        if (isMobile) {
            data.publication.base_maps = data.publication.base_maps.filter(bMap => bMap.value.indexOf('yandex') === -1);
        }

        if (__PROXY_URL__) {
            monkeyPatchConfig(config);
        }

        yield put(A.appSetConfig(config));
    }

    static* loadUserInfo() {
        const authToken = yield* select(state => state.app.authToken);

        if (authToken) {
            try {
                const { data: userInfo } = yield* call(getUserInfo, authToken);
                yield* put(A.setUserInfo(userInfo));
            } catch (e) {
                console.error(e);
                yield* put(A.setUserInfo(null));
            }
        }
    }

    @leading
    @operation('CAPTCHA')
    static* loadNewCaptcha() {
        let { data } = yield* call(loadCaptcha);

        return data;
    }

    @leading
    @operation('LOGIN')
    static* login(action: IAction<{ login: string; password: string; }>) {
        const { login, password } = action.payload;

        const result = yield* call(directLogin, login, password);
        const { token } = result.data;

        yield put(A.appSetAuthToken(token));
        setTimeout(() => {
            window.location.reload();
        });
    }

    @leading
    @operation('LOGOUT')
    static* logout() {
        const token = yield* select(getAuthToken);
        if (token) {
            const { data } = yield* call(directLogout, token);

            if ('token' in data) {
                yield put(A.appSetAuthToken(null));
                window.location.reload();
            }
        }
    }

    @every
    static* resetLoginOperation() {
        yield OperationsService.clearOperation({ type: '_', payload: 'LOGIN' });
    }

    // static* resetLogoutOperation() {
    //     yield OperationsService.clearOperation({ type: '_', payload: 'LOGOUT' });
    // }

    @every
    static* showObjectFiltersWindow() {
        yield put(A.appSetFiltersWindowVisible(true));
    }

    @every
    static* hideObjectFiltersWindow() {
        yield put(A.appSetFiltersWindowVisible(false));
    }

    @every
    static* refuseMobileApp() {
        yield put(A.refuseMobileApp());
    }

    @every
    @operation('FOLDER_STATE')
    static* setFolderState(action: IAction<{ id: number, state: boolean }>) {
        yield* call(OperationsService.updateData, 'FOLDER_STATE', {
            [action.payload.id]: action.payload.state
        });
    }
}


export const appService: AppService = new AppService();
export const actions = bindActionsTyped(AppService, appService);
appService.actions = actions;
